// Pure grid-layout helpers for the Insights dashboard, kept out of Insights.jsx so
// they can be unit-tested without pulling in react-grid-layout / d3.

export const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };

// PRD-1336 R1: a new widget's initial footprint is determined by its type — a
// single-value KPI should not land the same width as a time series. This is only
// the STARTING size; once placed, react-grid-layout owns the size and the user
// resizes/drags freely (that value persists, this seed does not). 12-col grid.
export const TYPE_FOOTPRINT = {
  // 4 wide, not 3: at 3 the header stamp and provenance footer wrap and leave the
  // number about a quarter of the card. It is one figure; give it room to read.
  kpi: { w: 4, h: 3 },
  summary_row: { w: 12, h: 3 },
  table: { w: 12, h: 5 },
  pie: { w: 4, h: 4 },
  doughnut: { w: 4, h: 4 },
};
// Bar / line / area / scatter / stacked_bar / grouped_bar / histogram, and anything
// unmapped, land at the standard time-series footprint.
export const DEFAULT_FOOTPRINT = { w: 6, h: 4 };

// The type-based footprint {w, h} for a widget, capped to the grid width.
export function footprintFor(widget, cols) {
  const fp = TYPE_FOOTPRINT[widget.widget_type_id] || DEFAULT_FOOTPRINT;
  return { w: Math.min(fp.w, cols), h: fp.h };
}

const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

// The first free spot for a w×h card: rows top-to-bottom, columns left-to-right. A
// card that only fits by running past the bottom edge still takes the gap; the
// bottom-left corner below everything always fits.
function firstFit(placed, width, height, cols) {
  const bottom = placed.reduce((max, p) => Math.max(max, p.y + p.h), 0);
  for (let y = 0; y < bottom; y++) {
    for (let x = 0; x + width <= cols; x++) {
      const cell = { x, y, w: width, h: height };
      if (!placed.some((p) => overlaps(p, cell))) return { x, y };
    }
  }
  return { x: 0, y: bottom };
}

// Build a layout array per breakpoint. Saved (user-dragged) positions are kept
// as-is; any widget WITHOUT one goes into the first gap that fits its footprint,
// falling back to a new row below everything.
//
// Gaps are found against the cards already placed, so placing a new widget never
// moves them: starting from the top-left would collide with them, and vertical
// compaction would then shove the whole dashboard around. A new widget fills the
// first gap rather than starting a fresh row, because widgets are saved one at a
// time and a fresh row each would stack them all in one column.
export function buildLayouts(widgets, saved) {
  const out = {};
  for (const bp of Object.keys(COLS)) {
    const cols = COLS[bp];
    const savedForBp = saved?.[bp] || [];
    const existingFor = (w) => savedForBp.find((l) => l.i === w.id);
    const placed = widgets.map(existingFor).filter(Boolean);
    out[bp] = widgets.map((w) => {
      const existing = existingFor(w);
      if (existing) return existing;
      const { w: width, h: height } = footprintFor(w, cols);
      const item = { i: w.id, ...firstFit(placed, width, height, cols), w: width, h: height, minW: 2, minH: 2 };
      placed.push(item);
      return item;
    });
  }
  return out;
}
